import { cn } from '../lib/cn.js';
import { Card, CardContent, CardHeader } from './card.js';
import { Skeleton } from './skeleton.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './table.js';

const FORM_FIELD_COUNT = 4;
const WEEK_DAYS = 7;
const TABLE_COLUMN_WIDTHS = ['w-40', 'w-28', 'w-32', 'w-24'] as const;
/** Décalage différent par jour : une semaine n'est pas sept barres identiques. */
const CALENDAR_EVENT_OFFSET = [
  'mt-6',
  'mt-16',
  'mt-10',
  'mt-24',
  'mt-8',
  'mt-20',
  'mt-14',
] as const;

function indexes(count: number): number[] {
  return Array.from({ length: count }, (_, index) => index);
}

/** Carte de formulaire : titre, description, lignes de champs. Le gabarit des Paramètres. */
export function FormCards({ count }: { count: number }) {
  return (
    <div className="space-y-4">
      {indexes(count).map((index) => (
        <Card key={index} data-testid="loading-state-form-card">
          <CardHeader>
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-72 max-w-full" />
          </CardHeader>
          <CardContent className="space-y-4">
            {indexes(FORM_FIELD_COUNT).map((field) => (
              <div key={field} className="space-y-2" data-testid="loading-state-form-field">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/**
 * Tuile d'annuaire, calquée sur la carte patient : avatar rond, nom, ligne plus courte
 * (âge), puis deux lignes de contact.
 */
export function TileGrid({ count }: { count: number }) {
  return (
    <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
      {indexes(count).map((index) => (
        <div
          key={index}
          className="rounded-xl border border-card-border bg-card p-4 shadow-sm sm:p-5"
          data-testid="loading-state-tile-card"
        >
          <div className="flex items-start justify-between gap-3 sm:gap-4">
            <div className="flex min-w-0 flex-1 items-start gap-3 sm:gap-4">
              <Skeleton
                className="h-10 w-10 shrink-0 rounded-full sm:h-12 sm:w-12"
                data-testid="loading-state-tile-avatar"
              />
              <div className="min-w-0 flex-1 space-y-2">
                <Skeleton className="h-4 w-2/3" data-testid="loading-state-tile-name" />
                <Skeleton className="h-3 w-1/3" data-testid="loading-state-tile-meta" />
              </div>
            </div>
            <Skeleton className="mt-1 h-4 w-4 shrink-0" />
          </div>
          <div className="mt-3 space-y-2 sm:mt-4">
            <Skeleton className="h-3 w-1/2" />
            <Skeleton className="h-3 w-2/5" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Ligne de liste : pastille ronde, titre, ligne secondaire plus courte. */
export function ListRows({ count }: { count: number }) {
  return (
    <div className="space-y-3">
      {indexes(count).map((index) => (
        <div key={index} className="flex items-center gap-4" data-testid="loading-state-list-row">
          <Skeleton className="h-10 w-10 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Tableau : en-tête de colonnes, puis des lignes de cellules — pas une barre pleine largeur. */
export function TableRows({ count }: { count: number }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {TABLE_COLUMN_WIDTHS.map((width) => (
            <TableHead key={width}>
              <Skeleton className={cn('h-3', width)} data-testid="loading-state-table-head" />
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {indexes(count).map((index) => (
          <TableRow key={index} data-testid="loading-state-table-row">
            {TABLE_COLUMN_WIDTHS.map((width, column) => (
              <TableCell key={width}>
                <Skeleton className={cn('h-4', column === 0 ? 'w-3/4' : 'w-2/3')} />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/**
 * Carte de contenu (widget, motif, questionnaire) : titre, ligne plus courte, pastilles,
 * bloc de corps. Pas un grand rectangle d'image.
 */
export function ContentCardGrid({ count }: { count: number }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {indexes(count).map((index) => (
        <Card key={index} data-testid="loading-state-grid-card">
          <CardHeader className="space-y-2">
            <div className="flex items-start justify-between gap-2">
              <Skeleton className="h-5 w-2/3" />
              <Skeleton className="h-5 w-14 rounded-full" />
            </div>
            <Skeleton className="h-3 w-1/2" />
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              <Skeleton className="h-5 w-16 rounded-full" />
              <Skeleton className="h-5 w-20 rounded-full" />
            </div>
            <Skeleton className="h-16 w-full" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/** Fiche : titre, sous-titre plus court, onglets, puis le corps. */
export function RecordSheet({ count }: { count: number }) {
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-2">
          <Skeleton className="h-8 w-64 max-w-full" data-testid="loading-state-fiche-title" />
          <Skeleton className="h-4 w-40 max-w-full" data-testid="loading-state-fiche-meta" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-9 w-24" />
          <Skeleton className="h-9 w-9" />
        </div>
      </div>
      <div className="flex gap-2 overflow-hidden">
        {indexes(6).map((tab) => (
          <Skeleton key={tab} className="h-9 w-24 shrink-0" data-testid="loading-state-fiche-tab" />
        ))}
      </div>
      <FormCards count={count} />
    </div>
  );
}

/** Semaine d'agenda : sept colonnes, un créneau par jour, pas des barres de tableau. */
export function WeekCalendar() {
  return (
    <div
      className="overflow-hidden rounded-xl border border-card-border bg-card"
      data-testid="loading-state-calendar"
    >
      <div className="grid grid-cols-7 border-b border-border">
        {indexes(WEEK_DAYS).map((day) => (
          <div key={day} className="flex justify-center py-3">
            <Skeleton className="h-4 w-8" />
          </div>
        ))}
      </div>
      <div className="grid h-96 grid-cols-7 divide-x divide-border">
        {indexes(WEEK_DAYS).map((day) => (
          <div key={day} className="p-1.5">
            <Skeleton className={cn('h-10 w-full', CALENDAR_EVENT_OFFSET[day])} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function TextLines({ count }: { count: number }) {
  return (
    <div className="space-y-2">
      {indexes(count).map((index) => (
        <Skeleton key={index} className="h-4 w-full" />
      ))}
    </div>
  );
}
